import { Alert, Stack } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { UserOptionalPropsAndPasswordTemp } from "api/types";
import { StyledButton } from "components/StyledButton";
import { unparse } from "papaparse";
import React, { useState } from "react";
const PREFIX = "users";
const classes = {
	info: `${PREFIX}-info`,
	table: `${PREFIX}-table`,
};

export interface CreateAccountsPageResultProps {
	users: UserOptionalPropsAndPasswordTemp[];
	goBackToPeoplePage: () => void;
}

export const CreateAccountsPageResult = ({
	users,
	goBackToPeoplePage,
}: CreateAccountsPageResultProps): JSX.Element => {
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);

	const handleChangePage = (
		event: React.MouseEvent<HTMLButtonElement> | null,
		newPage: number,
	): void => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	): void => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const downloadCSV = (): void => {
		const csv = unparse(users);

		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.setAttribute("href", url);
		link.setAttribute("download", "users.csv");
		link.click();
	};

	return (
		<Stack spacing={2}>
			<Alert severity="info" className={classes.info}>
				You have created the following accounts. Each account was created with
				the password you entered. <br />
				This page will be shown <strong>just once.</strong>
				<br />
				Either screenshot this page, or download the CSV.
			</Alert>

			<TableContainer component={Paper}>
				<Table aria-label="simple table" size="medium">
					<TableHead>
						<TableRow>
							<TableCell>Firstname</TableCell>
							<TableCell align="left">Lastname</TableCell>
							<TableCell align="left">Email</TableCell>
							<TableCell align="left">Role</TableCell>
							<TableCell align="left">Password</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{users.length > 0 &&
							users
								.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								.map((row, index) => {
									return (
										<TableRow
											key={index}
											sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
										>
											<TableCell component="th" scope="row">
												{row.firstName}
											</TableCell>
											<TableCell align="left">{row.lastName}</TableCell>
											<TableCell align="left">{row.email}</TableCell>
											<TableCell align="left">{row.role}</TableCell>
											<TableCell align="left" data-testid="password">
												{row.password}
											</TableCell>
										</TableRow>
									);
								})}
					</TableBody>
				</Table>
				<TablePagination
					rowsPerPageOptions={[5, 10, 25]}
					component="div"
					count={users.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</TableContainer>

			<Stack direction="row-reverse" spacing={2}>
				<StyledButton
					variant="contained"
					color="primary"
					size="large"
					onClick={downloadCSV}
					data-testid="download-csv-btn"
				>
					Download CSV
				</StyledButton>

				<StyledButton
					variant="contained"
					color="secondary"
					size="large"
					onClick={goBackToPeoplePage}
					data-testid="back-btn"
				>
					Back
				</StyledButton>
			</Stack>
		</Stack>
	);
};
